// LogSnag
import { LogSnag } from "@logsnag/next/server"
let logsnagClient: LogSnag | null = null
// Server-only token: in the browser (and anywhere LOGSNAG_TOKEN is unset) sends are a no-op.
// Built on first send so scripts that load env after import still pick it up.
function getLogsnag(): LogSnag | null {
    const token = process.env.LOGSNAG_TOKEN
    if (!token) return null
    if (!logsnagClient) logsnagClient = new LogSnag({ project: process.env.NEXT_PUBLIC_LOGSNAG_PROJECT, token })
    return logsnagClient
}

/** LogSnag rejects the WHOLE event (HTTP 400 "Tag values may not be longer than 160 characters")
 *  when any tag value is longer than this, so over-long strings are cut with an ellipsis. */
const MAX_TAG_VALUE_LENGTH = 160

function truncateTagValue<T>(value: T): T | string {
    if (typeof value !== "string" || value.length <= MAX_TAG_VALUE_LENGTH) return value
    let cut = value.slice(0, MAX_TAG_VALUE_LENGTH - 1)
    // Never leave half of a surrogate pair (emoji) dangling at the cut.
    if (/[\uD800-\uDBFF]$/.test(cut)) cut = cut.slice(0, -1)
    return `${cut}…`
}

/** Convert any key format (camelCase, PascalCase, snake_case, etc.) to lowercase kebab-case
 *  and ensure values are primitives. LogSnag API requires tag keys to contain only
 *  lowercase alphabet characters and dashes — no numbers, underscores, or spaces. */
export function sanitizeTags(data: Record<string, any>): Record<string, string | number | boolean> {
    return Object.fromEntries(
        Object.entries(data)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([key, value]) => [
                key
                    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")   // camelCase boundary (e.g. myKey -> my-Key)
                    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2") // consecutive caps (e.g. XMLParser -> XML-Parser)
                    .replace(/_/g, "-")             // snake_case -> snake-case
                    .replace(/\s+/g, "-")           // spaces -> dashes
                    .toLowerCase()                  // ensure lowercase
                    .replace(/[^a-z-]/g, "")        // strip everything except lowercase letters and dashes
                    .replace(/--+/g, "-")           // collapse double dashes
                    .replace(/^-/, "")              // strip leading dash
                    .replace(/-$/, ""),              // strip trailing dash
                truncateTagValue(typeof value === "object" ? JSON.stringify(value) : value)
            ])
            .filter(([key]) => key.length > 0)     // drop entries with empty keys after sanitization
    )
}

export class Notifications {
    /** Never rejects. Returns true when the LogSnag track call succeeded, false when it
     *  threw (swallowed) — consumers that persist alert-dedupe markers must check this. */
    public static async sendNotification(channel: string, title: string, icon = "💰", notify = false, userId?: string, data?: any): Promise<boolean> {
        console.log("🟡  Sending notification: ", title, " to channel: ", channel, " with data: ", data)
        const logsnag = getLogsnag()
        if (!logsnag) {
            console.error("[LOGSNAG.TS] LOGSNAG_TOKEN is not set; notification not sent:", title)
            return false
        }
        try {
            const tags = data ? sanitizeTags(data) : undefined
            await logsnag.track({
                channel: channel,
                event: title,
                ...(userId ? { user_id: userId } : {}),
                icon: icon,
                notify: notify,
                ...(tags ? { tags } : {})
            })
            return true
        } catch (error) {
            console.error("[LOGSNAG.TS] Error occurred while sending notification:", error)
            return false
        }
    }

    public static async sendTestNotification() {
        Notifications.sendNotification("testing-channel", "testing-event", "✨", true, "123", { hello: true, welcome: 1 })
    }
}
